/**
 * @module components/Layout/MainLayout
 * @description Layout raíz de la aplicación — orquesta el enrutamiento de vistas y la carga lazy.
 *
 * Renderiza el Header, GlobalSidebar, vista activa y DetailPanel (ISO 27002).
 * Todas las vistas excepto Dashboard y ACS se cargan con `React.lazy` + `Suspense`
 * para reducir el bundle inicial: el código de cada módulo se descarga solo cuando
 * el usuario navega a él por primera vez.
 *
 * ## Vistas gestionadas
 * dashboard | national-ra | network-ra | audit | audit-plans | findings |
 * corrective-actions | reports | assets | security-events | global-reports |
 * progress | acs | iso27001 | lmcs | cis | certifications | users | settings | help
 *
 * ## Acceso controlado por feature flags
 * Las vistas con feature flag desactivado redirigen al dashboard.
 * Ver `isViewEnabled()` en `config/features.ts`.
 *
 * @see GlobalSidebar para la navegación lateral con feature flags
 * @see config/features.ts para el mapeo de vistas a flags
 */
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Header } from './Header';
import { GlobalSidebar } from './GlobalSidebar';
import { DetailPanel } from './DetailPanel';
import { Dashboard } from '../Dashboard/Dashboard'; // Initial view — NOT lazy
import { ACSAlertsBanner, ACSSessionView } from '../ACS'; // Always rendered — NOT lazy

// Lazily loaded views (only needed when user navigates to them)
const NationalDashboard = lazy(() => import('../Dashboard/NationalDashboard'));
const NetworkDashboard = lazy(() => import('../Dashboard/NetworkDashboard'));
const ISO27001Dashboard = lazy(() => import('../ISO27001').then(m => ({ default: m.ISO27001Dashboard })));
const LMCSDashboard = lazy(() => import('../LMCS').then(m => ({ default: m.LMCSDashboard })));
const CISDashboard = lazy(() => import('../CIS').then(m => ({ default: m.CISDashboard })));
const AuditDashboard = lazy(() => import('../Audit').then(m => ({ default: m.AuditDashboard })));
const AuditPlansManager = lazy(() => import('../Audit').then(m => ({ default: m.AuditPlansManager })));
const FindingsManager = lazy(() => import('../Audit').then(m => ({ default: m.FindingsManager })));
const CorrectiveActionsPanel = lazy(() => import('../Audit').then(m => ({ default: m.CorrectiveActionsPanel })));
const ReportsManager = lazy(() => import('../Audit').then(m => ({ default: m.ReportsManager })));
const AuditLog = lazy(() => import('../Audit').then(m => ({ default: m.AuditLog })));
const AssetsDashboard = lazy(() => import('../Assets').then(m => ({ default: m.AssetsDashboard })));
const SecurityEventsDashboard = lazy(() => import('../SecurityEvents').then(m => ({ default: m.SecurityEventsDashboard })));
const ReportsDashboard = lazy(() => import('../Reports').then(m => ({ default: m.ReportsDashboard })));
const ProgressDashboard = lazy(() => import('../Reports').then(m => ({ default: m.ProgressDashboard })));
const ACSMeetingList = lazy(() => import('../ACS').then(m => ({ default: m.ACSMeetingList })));

const ViewLoader = () => (
  <div className="flex items-center justify-center h-full min-h-[200px]">
    <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
  </div>
);
// ═══════════════════════════════════════════════════════════════
// CERTIFICACIONES - MÓDULO TEMPORALMENTE DESACTIVADO
// Fecha: 11 de Noviembre, 2025
// Para reactivar: Descomentar el siguiente import
// import { CertificationsManager } from '../Certifications';
// ═══════════════════════════════════════════════════════════════
import { NotificationCenter } from '../Notifications/NotificationCenter';
import { SelectedControl } from '../../types/iso';
import { useData } from '../../contexts/DataContext';
import { useFramework } from '../../contexts/FrameworkContext'; // Para determinar framework activo
import { useOrganization } from '../../contexts/OrganizationContext'; // Para determinar vista inicial
import { LayoutDashboard, FileText, AlertTriangle, CheckCircle, Calendar, FileDown } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { Toast, ToastContainer } from '../common/Toast';
import { isViewEnabled } from '../../config/features';

// ═══════════════════════════════════════════════════════════════
// VISTAS DISPONIBLES
// - dashboard: Dashboard local de controles ISO
// - national-ra: Dashboard Nacional RA (Red Asistencial)
// - network-ra: Dashboard de Red RA
// - audit: Módulo de Auditoría
// - assets: Gestión de Activos
// - security-events: Registro de Eventos de Seguridad (ISO 27002 A.5.24-A.5.27, A.8.15-A.8.16)
// Para reactivar certifications: Agregar | 'certifications' al final
type View = 'dashboard' | 'national-ra' | 'network-ra' | 'audit' | 'assets' | 'security-events' | 'reports' | 'progress' | 'acs' | 'cis'; // | 'certifications'
// ═══════════════════════════════════════════════════════════════
type AuditSubView = 'audit-dashboard' | 'plans' | 'findings' | 'actions' | 'reports' | 'history';

export function MainLayout() {
  const { currentOrganization } = useOrganization();

  // SIEMPRE iniciar con el dashboard de controles ISO 27002
  // El usuario puede navegar manualmente a otras vistas usando la barra lateral
  const getInitialView = (): View => {
    return 'dashboard';
  };

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedControl, setSelectedControl] = useState<SelectedControl | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [currentView, setCurrentView] = useState<View>(getInitialView());
  const [auditSubView, setAuditSubView] = useState<AuditSubView>('audit-dashboard');
  const { isLoading: isLoadingData, controls } = useData();
  const { activeFramework } = useFramework();
  const { toasts, hideToast, success, error, warning, info } = useToast();

  // Determinar framework activo
  const isISO27001 = activeFramework?.code === 'ISO27001';
  const isLMCS = activeFramework?.code === 'LMCS';
  const isCIS = activeFramework?.code === 'CIS';

  // Exponer funciones de toast globalmente via context o prop drilling
  // Por ahora usaremos prop drilling para DetailPanel
  const toastFunctions = { success, error, warning, info };

  // ✅ NOTA: No actualizamos la vista automáticamente cuando cambia la organización
  // El usuario puede navegar manualmente entre vistas usando la barra lateral
  // y queremos respetar su selección

  // ✅ Sincronizar selectedControl con los datos actualizados del DataContext
  // Esto asegura que cuando se crea/actualiza/elimina una tarea, el DetailPanel se actualice
  useEffect(() => {
    if (selectedControl) {
      const updatedControl = controls.find(c => c.id === selectedControl.id);
      if (updatedControl) {
        // Preservar breadcrumb del control seleccionado actual
        setSelectedControl({
          ...updatedControl,
          breadcrumb: selectedControl.breadcrumb
        });
      }
    }
  }, [controls, selectedControl?.id]);

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleSelectControl = (control: SelectedControl | null) => {
    setSelectedControl(control);
    // Cambiar a vista dashboard si se selecciona un control
    if (control) {
      setCurrentView('dashboard');
    }
  };

  const handleViewChange = (view: View) => {
    // Si la vista está desactivada por feature flags, redirigir al dashboard
    if (!isViewEnabled(view)) {
      setCurrentView('dashboard');
      return;
    }
    setCurrentView(view);
    // Cerrar panel de detalle si se cambia de vista
    if (view !== 'dashboard') {
      setSelectedControl(null);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Loading Overlay - Muestra cuando DataContext está cargando */}
      {isLoadingData && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white px-4 py-2 text-sm flex items-center justify-center gap-2 shadow-lg">
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <span>Cargando datos...</span>
        </div>
      )}
      {/* ACS Alerts Banner - visible cuando hay alertas activas */}
      <ACSAlertsBanner />

      {/* Header */}
      <Header
        onToggleSidebar={handleToggleSidebar}
        sidebarCollapsed={sidebarCollapsed}
        onShowNotifications={() => setShowNotifications(true)}
        currentView={currentView}
        onViewChange={handleViewChange}
      />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Global Sidebar - Solo visible para ISO 27002, NO para ISO 27001, LMCS ni CIS */}
        {!isLMCS && !isISO27001 && !isCIS && (
          <GlobalSidebar
            collapsed={sidebarCollapsed}
            onToggleSidebar={handleToggleSidebar}
            currentView={currentView}
            onViewChange={handleViewChange}
            onSelectControl={handleSelectControl}
            selectedControl={selectedControl}
          />
        )}

        {/* Main Content Area */}
        <main className="flex-1 overflow-auto">
          <Suspense fallback={<ViewLoader />}>
          {currentView === 'cis' || (currentView === 'dashboard' && isCIS) ? (
            // Dashboard CIS Controls v8
            <CISDashboard />
          ) : currentView === 'dashboard' ? (
            // Renderizar dashboard según framework activo
            isISO27001 ? (
              // Dashboard ISO 27001
              <ISO27001Dashboard />
            ) : isLMCS ? (
              // Dashboard Ley Marco de Ciberseguridad - Vista limpia sin sidebar
              <LMCSDashboard />
            ) : selectedControl ? (
              // Dashboard ISO 27002 con control seleccionado
              <div className="h-full flex">
                <div className="flex-1">
                  <Dashboard onSelectControl={handleSelectControl} />
                </div>
                <DetailPanel
                  control={selectedControl}
                  onClose={() => setSelectedControl(null)}
                  toast={toastFunctions}
                />
              </div>
            ) : (
              // Dashboard ISO 27002 sin control seleccionado
              <Dashboard onSelectControl={handleSelectControl} />
            )
          ) : currentView === 'national-ra' ? (
            // Dashboard Nacional RA
            <NationalDashboard />
          ) : currentView === 'network-ra' ? (
            // Dashboard de Red RA
            <NetworkDashboard />
          ) : currentView === 'audit' ? (
            <div className="h-full flex flex-col">
              {/* Audit Submenu */}
              <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3">
                <div className="flex items-center gap-2">
                  {[
                    { id: 'audit-dashboard', label: 'Dashboard', icon: LayoutDashboard },
                    { id: 'plans', label: 'Planes', icon: Calendar },
                    { id: 'findings', label: 'Hallazgos', icon: AlertTriangle },
                    { id: 'actions', label: 'Acciones Correctivas', icon: CheckCircle },
                    { id: 'reports', label: 'Reportes', icon: FileDown },
                    { id: 'history', label: 'Historial', icon: FileText }
                  ].map((item) => {
                    const Icon = item.icon;
                    const isActive = auditSubView === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setAuditSubView(item.id as AuditSubView)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Audit Content */}
              <div className="flex-1 overflow-auto p-6">
                {auditSubView === 'audit-dashboard' && <AuditDashboard />}
                {auditSubView === 'plans' && <AuditPlansManager />}
                {auditSubView === 'findings' && <FindingsManager />}
                {auditSubView === 'actions' && <CorrectiveActionsPanel />}
                {auditSubView === 'reports' && <ReportsManager />}
                {auditSubView === 'history' && <AuditLog />}
              </div>
            </div>
          ) : currentView === 'assets' ? (
            <AssetsDashboard />
          ) : currentView === 'security-events' ? (
            // Módulo de Eventos de Seguridad - ISO 27002 A.5.24-A.5.27, A.8.15-A.8.16
            <SecurityEventsDashboard />
          ) : currentView === 'reports' ? (
            <ReportsDashboard />
          ) : currentView === 'progress' ? (
            <ProgressDashboard />
          ) : currentView === 'acs' ? (
            <ACSMeetingList />
          ) : /* ═══════════════════════════════════════════════════════════════
           * CERTIFICACIONES - MÓDULO TEMPORALMENTE DESACTIVADO
           * Para reactivar: Descomentar el siguiente bloque
           * ═══════════════════════════════════════════════════════════════
          currentView === 'certifications' ? (
            <div className="h-full overflow-auto p-6">
              <CertificationsManager />
            </div>
          ) : */ null}
          </Suspense>
        </main>
      </div>

      {/* Notification Center */}
      {showNotifications && (
        <NotificationCenter onClose={() => setShowNotifications(false)} />
      )}

      {/* Toast Notifications */}
      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            id={toast.id}
            type={toast.type}
            message={toast.message}
            description={toast.description}
            duration={toast.duration}
            onClose={hideToast}
          />
        ))}
      </ToastContainer>

      {/* ACS Session Overlay - Full-screen, visible cuando hay sesión activa */}
      <ACSSessionView />
    </div>
  );
}